import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { routeUrls } from "../../config/routeUrls";
import imageDocumentsDashboard from "../../assets/images/icons/documents-dashboard.svg";
import { DocumentDashboard as DocumentDashboardType } from "../../types/documents";
import LinkButton from "../ui/linkButton/LinkButton";
import { documentsService } from "../../api/documentsService/documentsService";

const DocumentsWidget: React.FC = () => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [documents, setDocuments] = useState<DocumentDashboardType[]>([]);

  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        const documentsRes = await documentsService.dashboardDocuments();
        if (documentsRes.success) {
          setDocuments(documentsRes.responseBody.documents);
        }
      } catch (error) {
        console.error('Error fetching documents:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDocuments();
  }, []);

  const renderSkeletonLoader = () => {
    return (
      <div className="py-4 border-b md:flex lg:block xl:flex md:justify-between md:items-center">
        <div>
          <div className="font-black text-red text-[40px] leading-[53px] animate-pulse bg-gray-200 h-[53px] w-16 rounded"></div>
          <div className="text-lg leading-[22px] animate-pulse bg-gray-200 h-6 w-48 rounded mt-2"></div>
        </div>
      </div>
    );
  };

  return (
    <div className="mb-[56px] md:mb-0 md:bg-lightGrey md:p-6 md:rounded card-shadow">
      <h2 className="font-light text-[24px] leading-[38px]">
        {t("screens.dashboard.widgets.documents.heading")}
      </h2>

      {isLoading ? (
        <>
          {renderSkeletonLoader()}
          {renderSkeletonLoader()}
        </>
      ) : (
        <>
          {documents.length > 0 && (
            <>
              {documents.map((document, index) => {
                return (
                  <div
                    key={index}
                    className="py-4 border-b md:flex lg:block xl:flex md:justify-between md:items-center"
                  >
                    <div>
                      {index < 2 && (
                        <div className="font-black text-red text-[40px] leading-[53px]">
                          {document.count}
                        </div>
                      )}

                      <p className="text-lg leading-[22px]">
                        {index >= 2 ? <strong>{document.count} </strong> : ""}
                        {document.count === 1
                          ? t(
                              "screens.dashboard.widgets.documents.documentPrefixes.singular"
                            )
                          : t(
                              "screens.dashboard.widgets.documents.documentPrefixes.plural"
                            )}{" "}
                        <strong>{document.status.toLowerCase()}</strong>
                      </p>
                    </div>

                    {index === 0 && (
                      <LinkButton
                        className="mt-4"
                        href={
                          routeUrls.documents + "?filter=" + document.status
                        }
                      >
                        {t("screens.dashboard.widgets.documents.button")}
                      </LinkButton>
                    )}
                  </div>
                );
              })}
            </>
          )}

          {documents.length === 0 && (
            <div className="mt-6">
              {t("screens.documents.emptyDocuments")}
            </div>
          )}
        </>
      )}

      <div className="mt-6">
        <Link
          className="inline-flex items-center"
          to={routeUrls.documents}
        >
          <img src={imageDocumentsDashboard} alt="services_icon_bag" />
          <div className="uppercase text-sm leading-[17px] underline text-red font-semibold ml-[6px] mb-[2px]">
            {t("screens.dashboard.widgets.documents.link")}
          </div>
        </Link>
      </div>
    </div>
  );
};

export default DocumentsWidget; 